import React, { useContext } from 'react';
import { styled } from '@mui/system';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';
import Table from 'shared-components/components/Table/Table';
import { doseRounding, groupBy, display, sortByNewestToOldest, computeGroupTotal } from 'op-utils';
import { LoadingSpinner } from 'shared-components/components';
import { GET_DOSE_SITE_SUMMARIES } from '../PatientProfileQueries';
import { useErrorModalContext, TenantContext } from 'op-contexts';
import { IDose, ITitleComponent, IFormattedDose } from './interfaces';
import { useTheme } from '@mui/material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';

const TitleStatus = styled('div')<any>`
  line-height: 24px;
  ${(props: any) => props.$grouped && 'padding-left: 16px;'}
`;

const StatusContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const StatusCircle = styled('div')<any>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ statusColor }: { statusColor: string }) => `${statusColor}`};
  margin-right: 2px;
`;

const tableHeaders = [
  { title: 'Dose site summary', key: 'title', type: 'string', width: '26%' },
  { title: 'Course', key: 'course', type: 'string', width: '11%' },
  { title: 'Delivered', key: 'delivered', type: 'string', width: '11%' },
  { title: 'Prescribed', key: 'prescribed', type: 'string', width: '11%' },
  { title: 'Start Tx', key: 'treatmentStart', type: 'string', width: '11%' },
  { title: 'Latest Tx', key: 'treatmentLatest', type: 'string', width: '11%' },
  { title: 'Final Tx', key: 'treatmentFinal', type: 'string', width: '11%' },
  { title: 'Source', key: 'source', type: 'string', width: '8%' },
];

const createDoseGroup = (groupName: string, doses: Array<any>) => {
  const doseGroupData = sortByNewestToOldest(doses);
  const showTotal = doseGroupData.length > 1;
  const { totalDeliveredDose, totalDoseMax } = computeGroupTotal(doseGroupData);

  return {
    groupName: groupName,
    totalDeliveredDose: totalDeliveredDose,
    totalDoseMax: totalDoseMax,
    isAria: true,
    sortDate: doseGroupData[0].sortDate,
    showTotal: showTotal,
    doseGroupCards: doseGroupData,
  };
};

const DoseSiteSummarySimple = (): JSX.Element => {
  const match = useRouteMatch<any>();
  const { setError } = useErrorModalContext();
  const { id: patientId } = match.params;
  const { data, loading, error } = useQuery(GET_DOSE_SITE_SUMMARIES, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
  });
  if (loading) return <LoadingSpinner loadingText={'Loading dose site summaries'} relativeSpinner={true} />;
  if (error) {
    // TODO - add context-based local error handling for the specific error we want to deal with
    setError(null);
    return <DoseSiteSummary doseSiteSummaries={[]} />;
  }
  const doseSiteSummaries = data?.doseSiteSummaries || [];
  return <DoseSiteSummary doseSiteSummaries={doseSiteSummaries} />;
};

const DoseSiteSummary = ({ doseSiteSummaries }: any): JSX.Element => {
  const theme = useTheme();
  const tenantContext = useContext(TenantContext);
  const doseUnit = tenantContext.doseUnit;
  const allDoses: any = [];
  const mqDoses = doseSiteSummaries
    .filter((dss: IDose) => !dss.isAria)
    .map((element: any) => {
      return {
        ...element,
        sortDate: moment(element.latestTreatment).format(CurrentAppConfig.DoseSiteSummary.DateFormat),
      };
    });

  const ariaDoses = doseSiteSummaries
    .filter((dss: IDose) => dss.isAria)
    .map((element: any) => {
      return {
        ...element,
        sortDate: moment(element.latestTreatment).format(CurrentAppConfig.DoseSiteSummary.DateFormat),
      };
    });

  const groupedDose = groupBy(ariaDoses, 'categorySiteGroup');
  const formattedAria: Array<any> = [];
  Object.entries(groupedDose).forEach((element: any) => {
    formattedAria.push(createDoseGroup(element[0], element[1]));
  });

  const getStatusColor = (status: string): string =>
    status.toUpperCase() === 'IN PROGRESS'
      ? theme.palette.warning.dark
      : status.toUpperCase() === 'COMPLETE'
      ? theme.palette.primary.main
      : theme.palette.grey[600];

  const TitleComponent = ({ title, status, grouped }: ITitleComponent): JSX.Element => (
    <TitleStatus $grouped={grouped}>
      <div>{display(title)}</div>
      {status && (
        <StatusContainer>
          <StatusCircle statusColor={getStatusColor(status)} />
          {status}
        </StatusContainer>
      )}
    </TitleStatus>
  );

  const getAriaStatus = (dose: IDose) => {
    const remainingDose = dose?.remainingDoseQuantity || 0;
    let status = 'In progress';

    if (dose.statusReason === 'CompletedEarly') {
      status = 'Completed early';
    } else if (remainingDose === 0.0) {
      status = 'Complete';
    }
    return status;
  };

  const formatDate = (date: string) => {
    return date ? moment(date).format(CurrentAppConfig.DoseSiteSummary.DateFormat) : '-';
  };

  const TableData = ({ data }: { data: string | null }): JSX.Element => <div>{display(data)}</div>;

  const generateDoseRow = (dose: IFormattedDose) => {
    const {
      title,
      status,
      course,
      delivered,
      prescribed,
      treatmentStart,
      treatmentLatest,
      treatmentFinal,
      source,
      grouped,
    } = dose;

    const doseObject = {
      rows: [
        {
          key: 'title',
          component: <TitleComponent title={title} status={status} grouped={grouped} />,
          width: '26%',
        },
        { key: 'course', component: <TableData data={course} />, width: '11%' },
        {
          key: 'delivered',
          component: <TableData data={delivered} />,
          width: '11%',
        },
        {
          key: 'prescribed',
          component: <TableData data={prescribed} />,
          width: '11%',
        },
        { key: 'treatmentStart', component: <TableData data={treatmentStart} />, width: '11%' },
        { key: 'treatmentLatest', component: <TableData data={treatmentLatest} />, width: '11%' },
        {
          key: 'treatmentFinal',
          component: <TableData data={treatmentFinal} />,
          width: '11%',
        },
        {
          key: 'source',
          component: <TableData data={source} />,
          width: '8%',
        },
      ],
    };

    if (status?.toUpperCase() === 'IN PROGRESS') {
      //@ts-ignore
      doseObject['rowBackgroundColor'] = theme.palette.warning.light;
    }

    allDoses.push(doseObject);
  };

  const generateAriaDoseRow = ({ dose, grouped }: { dose: IDose; grouped?: boolean }) => {
    const actualDose = dose.deliveredDosageQuantity || 0;
    const remainingDose = dose.remainingDoseQuantity || 0;
    const totalDose = actualDose + remainingDose;
    const actualFraction = dose.deliveredDosageRatio || 0;
    const totalFraction = dose.plannedDosageRatio || 0;
    const status = getAriaStatus(dose);
    const lastTreatment = dose.latestTreatment ? formatDate(dose.latestTreatment) : null;
    const finalTx = status.startsWith('Complete') ? lastTreatment : 'See Appointments';

    const attr = {
      title: dose?.identifierSite,
      course: dose?.medicationReference,
      delivered: `${doseRounding(actualDose, true, tenantContext)}${doseUnit}/${actualFraction}#`,
      prescribed: `${doseRounding(totalDose, true, tenantContext)}${doseUnit}/${totalFraction}#`,
      treatmentStart: dose.firstTreatment ? formatDate(dose.firstTreatment) : null,
      treatmentLatest: lastTreatment,
      treatmentFinal: finalTx,
      source: 'Aria',
      status: status,
      grouped: grouped,
    };
    generateDoseRow(attr);
  };

  mqDoses.forEach((dose: any) => {
    const actualDose = doseRounding(dose.deliveredDosageQuantity, false, tenantContext);
    const totalDose = doseRounding(dose.plannedDosageQuantity, false, tenantContext);
    const actualFraction = dose.deliveredDosageRatio || 0;
    const totalFraction = dose.plannedDosageRatio || 0;
    const status = actualFraction === totalFraction ? 'Complete' : 'In progress';
    const lastTreatment = formatDate(dose.latestTreatment);
    const finalTx = status.startsWith('Complete') ? lastTreatment : 'See Appointments';

    const attr = {
      title: dose.identifierSite,
      status: status,
      course: dose?.medicationReference,
      delivered: `${actualDose}${doseUnit}/${actualFraction}#`,
      prescribed: `${totalDose}${doseUnit}/${totalFraction}#`,
      treatmentStart: formatDate(dose.firstTreatment),
      treatmentLatest: formatDate(dose.latestTreatment),
      treatmentFinal: finalTx,
      source: 'Mosaiq',
    };
    generateDoseRow(attr);
  });

  formattedAria.forEach((group: any) => {
    if (group.doseGroupCards.length > 1) {
      const attr = {
        title: group?.groupName,
        course: display(null),
        delivered: group?.showTotal && group?.totalDeliveredDose && `${group.totalDeliveredDose}${doseUnit}`,
        prescribed: group?.showTotal && group?.totalDoseMax && `${group.totalDoseMax}${doseUnit}`,
        treatmentStart: display(null),
        treatmentLatest: display(null),
        treatmentFinal: display(null),
        source: 'Aria',
      };
      generateDoseRow(attr);

      group.doseGroupCards.forEach((dose: IDose) => {
        generateAriaDoseRow({ dose, grouped: true });
      });
    } else {
      const dose = group.doseGroupCards[0];
      generateAriaDoseRow({ dose });
    }
  });

  const tableData = allDoses.map((dose: any) => {
    return {
      tableRows: dose.rows,
      ...dose,
    };
  });
  return <Table tableHeaders={tableHeaders} tableData={tableData} noRowsText="Treatment has not started yet." />;
};

export default DoseSiteSummarySimple;
